import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Search, 
  ArrowDownLeft, 
  ArrowUpRight, 
  RefreshCw, 
  AlertTriangle,
  Layers
} from 'lucide-react';
import { useInventoryTransactions } from '@/hooks/useInventory';
import { TRANSACTION_TYPE_LABELS, type TransactionType } from '@/types/database/inventory';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const transactionTypeIcons: Record<TransactionType, React.ReactNode> = {
  inbound: <ArrowDownLeft className="h-4 w-4 text-primary" />,
  outbound: <ArrowUpRight className="h-4 w-4 text-secondary-foreground" />,
  adjustment: <RefreshCw className="h-4 w-4 text-accent-foreground" />,
  damage: <AlertTriangle className="h-4 w-4 text-destructive" />,
};

const transactionTypeBadgeVariants: Record<TransactionType, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  inbound: 'default',
  outbound: 'secondary',
  adjustment: 'outline',
  damage: 'destructive',
};

interface TransactionHistoryProps {
  productId?: string;
  limit?: number;
  currentStock?: number;
  unitOfMeasure?: string;
}

export function TransactionHistory({ productId, limit, currentStock, unitOfMeasure }: TransactionHistoryProps) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const { data: transactions, isLoading } = useInventoryTransactions({
    productId,
    transactionType: typeFilter !== 'all' ? typeFilter : undefined,
    limit,
  });

  const filteredTransactions = transactions?.filter((tx) =>
    tx.product?.name?.toLowerCase().includes(search.toLowerCase()) ||
    tx.reference_number?.toLowerCase().includes(search.toLowerCase()) ||
    tx.notes?.toLowerCase().includes(search.toLowerCase())
  );

  // Compute running balance (transactions are newest-first)
  // Start from current stock and subtract each transaction's effect going backwards
  const showBalance = productId && currentStock !== undefined;
  const transactionsWithBalance = (() => {
    if (!filteredTransactions || !showBalance) return filteredTransactions?.map(tx => ({ ...tx, balance: undefined as number | undefined }));
    let balance = currentStock;
    return filteredTransactions.map(tx => {
      const currentBalance = balance;
      // Reverse the transaction effect to get prior balance
      const qty = tx.quantity;
      const isAddition = tx.transaction_type === 'inbound' || 
        (tx.transaction_type === 'adjustment' && qty > 0);
      if (isAddition) {
        balance = balance - Math.abs(qty);
      } else {
        balance = balance + Math.abs(qty);
      }
      return { ...tx, balance: currentBalance };
    });
  })();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search transactions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Transaction type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(TRANSACTION_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              {showBalance && <TableHead className="text-right">Balance</TableHead>}
              <TableHead className="text-right">Unit Cost</TableHead>
              <TableHead className="text-right">Total Value</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactionsWithBalance?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showBalance ? 9 : 8} className="text-center text-muted-foreground py-8">
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              transactionsWithBalance?.map((tx) => {
                const hasUnitCost = tx.unit_cost !== null && tx.unit_cost > 0;
                const totalValue = hasUnitCost ? tx.quantity * tx.unit_cost! : null;
                const isBatchTracked = hasUnitCost && (tx.transaction_type === 'inbound' || tx.transaction_type === 'outbound' || tx.transaction_type === 'damage');
                
                return (
                  <TableRow key={tx.id}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {format(new Date(tx.created_at), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell className="font-medium">
                      {tx.product?.name || 'Unknown Product'}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={transactionTypeBadgeVariants[tx.transaction_type]}
                        className="gap-1"
                      >
                        {transactionTypeIcons[tx.transaction_type]}
                        {TRANSACTION_TYPE_LABELS[tx.transaction_type]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {(() => {
                        const isNegative = tx.transaction_type === 'outbound' || tx.transaction_type === 'damage' || 
                          (tx.transaction_type === 'adjustment' && tx.quantity < 0);
                        const prefix = isNegative ? '-' : '+';
                        const displayQty = Math.abs(tx.quantity).toLocaleString();
                        return (
                          <span className={isNegative ? 'text-destructive' : 'text-primary'}>
                            {prefix}{displayQty} {unitOfMeasure || 'kg'}
                          </span>
                        );
                      })()}
                    </TableCell>
                    {showBalance && (
                      <TableCell className="text-right font-mono font-medium">
                        {tx.balance !== undefined ? (
                          <span>{tx.balance.toLocaleString()} {unitOfMeasure || 'kg'}</span>
                        ) : '-'}
                      </TableCell>
                    )}
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {hasUnitCost ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="flex items-center justify-end gap-1">
                              {isBatchTracked && <Layers className="h-3 w-3 text-primary" />}
                              {formatCurrency(tx.unit_cost!)}
                            </TooltipTrigger>
                            <TooltipContent>
                              {isBatchTracked ? 'FIFO batch-tracked cost' : 'Procurement cost'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {totalValue !== null ? (
                        <span className={tx.transaction_type === 'outbound' || tx.transaction_type === 'damage' || (tx.transaction_type === 'adjustment' && tx.quantity < 0) ? 'text-destructive' : 'text-primary'}>
                          {formatCurrency(totalValue)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {tx.reference_number || '-'}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-muted-foreground">
                      {tx.notes || '-'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
